import React from 'react';
import { Button } from '@daater/ui';
import { useNavigate } from '@tanstack/react-router';
import { usePaymentHistoryQuery } from '../api/queries';
import {
  buildPageNumbers,
  filterCompletedPayments,
  formatPaymentHistoryAmount,
  formatPaymentHistoryDate,
  getPerformerName,
} from './payment-history.utils';

export default function PaymentHistoryScreen() {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  const { data, isLoading, isFetching, error } = usePaymentHistoryQuery(page);

  const history = React.useMemo(() => filterCompletedPayments(data?.items ?? []), [data?.items]);
  const totalItems = data?.total_items ?? history.length;
  const perPage = data?.per_page ?? 10;
  const currentPage = data?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const pages = buildPageNumbers(currentPage, totalPages);

  return (
    <section className="mx-auto w-full max-w-6xl p-4 sm:p-6">
      <div className="rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-charcoal-grey">Pagos</h1>
            <p className="mt-1 text-sm text-slate">Transacciones: {history.length}</p>
          </div>
          <Button onClick={() => void navigate({ to: '/payment' })}>Realizar un pago</Button>
        </div>

        {isLoading ? (
          <div className="mt-6 rounded-lg border border-pale-grey-three bg-pale-grey p-4 text-sm text-slate">
            Cargando historial de pagos...
          </div>
        ) : null}

        {error ? (
          <div className="mt-6 rounded-lg border border-darkish-pink/30 bg-darkish-pink/10 p-4 text-sm text-darkish-pink">
            Ocurrió un error cargando el historial de pagos.
          </div>
        ) : null}

        {!isLoading && !error ? (
          <div className="mt-6 overflow-x-auto">
            {history.length === 0 ? (
              <div className="rounded-lg border border-pale-grey-three bg-pale-grey p-4 text-sm text-slate">
                No tienes ningún pago registrado.
              </div>
            ) : (
              <table className="min-w-full divide-y divide-pale-grey-three text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate">
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Realizado por</th>
                    <th className="px-3 py-2">Valor</th>
                    <th className="px-3 py-2">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pale-grey-three">
                  {history.map(item => (
                    <tr key={item._id} className="text-charcoal-grey">
                      <td className="px-3 py-2">{formatPaymentHistoryDate(item.createdAt)}</td>
                      <td className="px-3 py-2">{getPerformerName(item)}</td>
                      <td className="px-3 py-2">${formatPaymentHistoryAmount(item.amount)}</td>
                      <td className="px-3 py-2">Pago</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : null}

        {totalPages > 1 ? (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage <= 1 || isFetching}
            >
              Anterior
            </Button>
            {pages.map(p => (
              <Button
                key={p}
                size="sm"
                variant={p === currentPage ? 'default' : 'outline'}
                onClick={() => setPage(p)}
                disabled={isFetching}
              >
                {p}
              </Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages || isFetching}
            >
              Siguiente
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
